import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { INSPECTION, COMPLIANCE, AI } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import InspectionModal from '@/components/InspectionModal';
import ComplianceModal from '@/components/ComplianceModal';
import AIAnalysisModal from '@/components/AIAnalysisModal';
import PhotoUploadGallery from '@/components/PhotoUploadGallery';
import { FileText, Sparkles, Shield, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [activeTab, setActiveTab] = useState('inspections');
  const [loading, setLoading] = useState(true);
  
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    inspection_type: 'structural',
    findings: '',
    recommendations: '',
    severity: 'medium'
  });
  
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [complianceType, setComplianceType] = useState('building_permit');
  
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiForm, setAIForm] = useState({
    description: '',
    analysis_type: 'defect',
    image_base64: null
  });
  const [aiResult, setAIResult] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchProjectData = useCallback(async () => {
    try {
      const [projectRes, inspectionsRes, checklistsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inspections/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/compliance/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProject(projectRes.data);
      setInspections(inspectionsRes.data);
      setChecklists(checklistsRes.data);
    } catch (error) {
      console.error('Failed to fetch project data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const handleCreateInspection = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inspections`, 
        { ...inspectionForm, project_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowInspectionModal(false);
      setInspectionForm({
        inspection_type: 'structural',
        findings: '',
        recommendations: '',
        severity: 'medium'
      });
      fetchProjectData();
    } catch (error) {
      console.error('Failed to create inspection:', error);
    }
  };

  const handleCreateCompliance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/compliance`,
        { project_id: id, checklist_type: complianceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowComplianceModal(false);
      fetchProjectData();
    } catch (error) {
      console.error('Failed to create compliance checklist:', error);
    }
  };

  const handleAIAnalysis = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    setAIResult('');
    try {
      const response = await axios.post(`${API}/ai/analyze`,
        aiForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAIResult(response.data.analysis);
    } catch (error) {
      console.error('AI analysis failed:', error);
      setAIResult('Analysis failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        setAIForm({ ...aiForm, image_base64: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-7xl mx-auto px-6 py-8 mt-16 text-center">
          <p className="text-slate-600">Project not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2 text-slate-600 hover:text-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>

        <div className="grid-border bg-white p-8 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-black mb-2" style={{fontFamily: 'Outfit'}}>
                {project.title}
              </h1>
              <p className="text-slate-600">{project.description}</p>
              <p className="text-sm text-slate-500 mt-2">{project.property_address}</p>
            </div>
            <div className="flex gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm">
                {project.property_type}
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-sm">
                {project.status}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <button
              data-testid={INSPECTION.createButton}
              onClick={() => setShowInspectionModal(true)}
              className="flex items-center gap-3 p-4 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
            >
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-medium">New Inspection</span>
            </button>
            <button
              data-testid={COMPLIANCE.createButton}
              onClick={() => setShowComplianceModal(true)}
              className="flex items-center gap-3 p-4 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
            >
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Compliance Checklist</span>
            </button>
            <button
              data-testid={AI.analyzeButton}
              onClick={() => setShowAIModal(true)}
              className="flex items-center gap-3 p-4 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all"
            >
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">AI Analysis</span>
            </button>
          </div>
        </div>

        <div className="grid-border bg-white mb-6">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('inspections')}
              className={'px-6 py-4 font-medium transition-all ' + (activeTab === 'inspections' ? 'border-b-2 border-black text-black' : 'text-slate-600 hover:text-black')}
            >
              Inspections ({inspections.length})
            </button>
            <button
              onClick={() => setActiveTab('compliance')}
              className={'px-6 py-4 font-medium transition-all ' + (activeTab === 'compliance' ? 'border-b-2 border-black text-black' : 'text-slate-600 hover:text-black')}
            >
              Compliance ({checklists.length})
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'inspections' && (
              <div className="space-y-4">
                {inspections.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No inspections yet. Create your first inspection.</p>
                  </div>
                ) : (
                  inspections.map((inspection) => (
                    <div key={inspection.id} className="grid-border bg-slate-50 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold mb-1" style={{fontFamily: 'Outfit'}}>
                            {inspection.inspection_type.replace('_', ' ').toUpperCase()}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {new Date(inspection.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={'px-3 py-1 text-xs rounded-sm ' + (inspection.severity === 'high' || inspection.severity === 'critical' ? 'bg-red-100 text-red-800' : inspection.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800')}>
                          {inspection.severity}
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-black mb-1">Findings:</p>
                          <p className="text-sm text-slate-600">{inspection.findings}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-black mb-1">Recommendations:</p>
                          <p className="text-sm text-slate-600">{inspection.recommendations}</p>
                        </div>
                        <div className="pt-3 border-t border-slate-200">
                          <PhotoUploadGallery
                            inspectionId={inspection.id}
                            existingPhotos={inspection.photos || []}
                            onUploadComplete={fetchProjectData}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'compliance' && (
              <div className="space-y-4">
                {checklists.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No compliance checklists yet. Create your first checklist.</p>
                  </div>
                ) : (
                  checklists.map((checklist) => (
                    <div key={checklist.id} className="grid-border bg-slate-50 p-6">
                      <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit'}}>
                        {checklist.checklist_type.replace('_', ' ').toUpperCase()}
                      </h3>
                      <div className="space-y-2">
                        {checklist.items.map((item, index) => (
                          <div key={`${checklist.id}-${item.item}`} className="flex items-start gap-3 p-3 bg-white rounded-sm">
                            <input
                              type="checkbox"
                              checked={item.status === 'completed'}
                              readOnly
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-black">{item.item}</p>
                              {item.notes && (
                                <p className="text-xs text-slate-500 mt-1">{item.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <InspectionModal
        open={showInspectionModal}
        form={inspectionForm}
        setForm={setInspectionForm}
        onSubmit={handleCreateInspection}
        onClose={() => setShowInspectionModal(false)}
      />

      <ComplianceModal
        open={showComplianceModal}
        complianceType={complianceType}
        setComplianceType={setComplianceType}
        onSubmit={handleCreateCompliance}
        onClose={() => setShowComplianceModal(false)}
      />

      <AIAnalysisModal
        open={showAIModal}
        form={aiForm}
        setForm={setAIForm}
        onSubmit={handleAIAnalysis}
        onClose={() => {
          setShowAIModal(false);
          setAIResult('');
          setAIForm({ description: '', analysis_type: 'defect', image_base64: null });
        }}
        onImageUpload={handleImageUpload}
        loading={aiLoading}
        result={aiResult}
      />
    </div>
  );
}
